import Head from 'next/head';
import Image from 'next/future/image';
import Link from 'next/link';
import router from 'next/router';
import cookie from 'js-cookie';
import { useEffect, useState } from 'react';
import { CtaButton } from '@/components/Cta/Cta';
import chevRight from '@/public/icons/product/chevronRight.svg';
import { getShortenCountryName } from '@/lib/use-origin-country';
import topShape from '@/public/images/help/shape1.svg';
import bottomShape from '@/public/images/help/shape2.svg';
import shopifyApi from '@/lib/shopify';
import contactUsConfig from '@/settings/configs/help';
import ContactUsForm from '@/components/help/contact-us-form';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import { useCustomerContext } from '@/lib/customer/CustomerContext';

type SelfHelpProps = {
  title: string;
  description: string;
  link: string;
};

type FaqLinks = {
  title: string;
  link: string;
};

type FaqProps = {
  title: string;
  links: Array<FaqLinks>;
};

async function getCustomerName() {
  const token = cookie.get('auth-token');
  // get username from Shopify if user is login
  const response = await shopifyApi.fetch({
    query: `query($token: String!){
                customer(customerAccessToken:$token) {
                  firstName
                }
              }`,
    variables: { token },
  });

  if (response.customer == null) return '';

  return response.customer.firstName;
}
const SelfHelpBox = ({ title, description, link }: SelfHelpProps) => (
  <Link href={link} passHref>
    <a>
      <div className="group flex h-full flex-col items-center justify-start rounded-md border border-gray-700 bg-white px-8 py-6 hover:cursor-pointer hover:drop-shadow-xl xs:px-6 xs:py-8 lg:px-10 lg:py-12">
        <b className="mb-1 text-center group-hover:underline group-hover:underline-offset-4 xs:mb-2 group-hover:xs:underline-offset-8 lg:mb-3">
          {title}
        </b>
        <p className="text-center !leading-tight">{description}</p>
      </div>
    </a>
  </Link>
);

const FaqLink = ({ title, links }: FaqProps) => (
  <div className="text-lg">
    <b className="mb-3 block lg:mb-4">{title}</b>
    <div className="flex flex-col space-y-5 md:space-y-3">
      {links.map((l, index) => (
        <a
          key={index}
          href={l.link}
          className="relative pr-4 text-[15px] !leading-tight xs:text-base md:pr-0"
        >
          {l.title}
          <div className="absolute right-0 top-1/2 block w-[8px] -translate-y-1/2 xs:w-[10px] md:hidden">
            <Image src={chevRight} alt="expand" className="w-full" />
          </div>
        </a>
      ))}
    </div>
  </div>
);

export default function ContactUs() {
  const { customer } = useCustomerContext();
  const [userName, setUserName] = useState('');

  useEffect(() => {
    if (customer) {
      getCustomerName().then((val) => {
        setUserName(val);
      });
    }
  }, [customer]);

  return (
    <>
      <Head>
        <title>{`Help - Reebelo ${getShortenCountryName()}
      `}</title>
        <WebsiteSchema />
        <meta
          name="description"
          content="Our mission is to refresh the way we all consume tech. We believe in a future where everyone has access to affordable electronics. Delivered through a platform built on sustainable values and quality-assured devices. Whether it's refurbished or new, buying or selling, it's time to say hello to Reebelo - APAC's fastest"
        ></meta>
      </Head>
      <div className="bg-teal-500 pt-0 md:py-16">
        <div className="reebelo-container">
          <section className="">
            {/* eslint-disable-next-line tailwindcss/enforces-negative-arbitrary-values */}
            <div className="relative -ml-[1.5rem] w-[calc(100%+2.75rem)] rounded-md bg-teal-500 px-6 py-8 ring-0 ring-[#D5FBFC] drop-shadow-none xxs:py-10 xs:py-12 sm:-ml-[2rem] sm:w-[calc(100%+4rem)] md:ml-auto md:w-full md:bg-white md:pr-20 md:ring-[6px] md:drop-shadow-xl lg:px-8">
              <h1 className="mb-1 text-[22px] font-bold !leading-tight text-gray-700 xxs:text-2xl">
                {`Welcome to Reebelo's Customer Service${
                  customer && userName !== '' ? `, ${userName}` : ``
                }`}
              </h1>
              <div className="mb-3">
                <p className="!leading-tight">
                  For faster & more personalized help, please contact us
                  directly via{' '}
                  <Link
                    href={`/account/${customer ? 'orders' : 'register'}`}
                    passHref
                  >
                    <a className="font-bold underline">your customer account</a>
                  </Link>
                  , using the Help, Return, or Warranty buttons.
                </p>
                {!customer && (
                  <p className="!leading-tight">
                    No account?{' '}
                    <Link href="/account/register" passHref>
                      <a className="font-bold underline">Create one</a>
                    </Link>{' '}
                    in a minute to fetch your past orders.
                  </p>
                )}
                <br />
                <p className="!leading-tight">
                  If you haven't placed any order yet, you can use the form
                  below and our team will get back to you within two business
                  days.
                </p>
              </div>
              <CtaButton
                variant="dark"
                className="min-w-full xs:min-w-[180px]"
                onClick={() => {
                  if (customer) router.push(`/account/orders`);
                  else router.push(`/account/register`);
                }}
              >
                {`${customer ? `Go to My Account` : `Create an Account`}`}
              </CtaButton>
              {!customer && (
                <p className="mt-3 !leading-tight">
                  Already a member? Log-in{' '}
                  <Link href="/account/login" passHref>
                    <a className="font-bold underline">here</a>
                  </Link>
                  .
                </p>
              )}
              <div className="absolute top-0 right-0 hidden w-24 md:block lg:w-28">
                <Image src={topShape} alt="Contact Us" className="w-full" />
              </div>
              <div className="absolute bottom-0 right-0 hidden w-24 md:block lg:w-28">
                <Image src={bottomShape} alt="Contact Us" className="w-full" />
              </div>
            </div>
          </section>
          {/* eslint-disable-next-line tailwindcss/enforces-negative-arbitrary-values */}
          <section className="mt-0 -ml-[1.5rem] w-[calc(100%+2.75rem)] bg-[#EAFDFF] px-6 py-8 xxs:py-10 xs:py-12 sm:-ml-[2rem] sm:w-[calc(100%+4rem)] md:ml-auto md:mt-16 md:w-full md:bg-teal-500 md:px-0 md:py-0">
            <h3 className="mb-4 text-center text-xl font-bold text-gray-700 md:mb-2 md:text-left">
              Self-Help Articles
            </h3>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3 lg:gap-6">
              <SelfHelpBox
                title="About Reebelo"
                description="Learn more about Reebelo & our sustainable mission."
                link={contactUsConfig.about.link}
              />
              <SelfHelpBox
                title="Shipping Enquiries"
                description="How to track your order, report a lost shipment, change your delivery address & more."
                link={contactUsConfig.shipping.link}
              />
              <SelfHelpBox
                title="My Account"
                description="How to create your account, reset your password, edit your settings & more."
                link={contactUsConfig.account.link}
              />
              <SelfHelpBox
                title="Warranty & Returns"
                description="How to return, replace or refund your order."
                link={contactUsConfig.warranty.link}
              />
              <SelfHelpBox
                title="Payment Enquiries"
                description="Learn more about payment methods & issues that may arise."
                link={contactUsConfig.payment.link}
              />
              <SelfHelpBox
                title="Other Enquiries"
                description="Other enquiries that you would like to know more about."
                link={contactUsConfig.other.link}
              />
            </div>
          </section>
        </div>
      </div>
      <div className="reebelo-container py-8 xxs:py-12 md:py-16">
        <h2 className="mb-3 text-[22px] font-bold text-gray-700 xxs:mb-4 xxs:text-2xl xs:mb-6">
          Frequently Asked Questions
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
          <FaqLink
            title="About Reebelo"
            links={contactUsConfig.about.faqLinks}
          />
          <FaqLink title="Account" links={contactUsConfig.account.faqLinks} />
          <FaqLink title="Shipping" links={contactUsConfig.shipping.faqLinks} />
          <FaqLink
            title="Warranty & Returns"
            links={contactUsConfig.warranty.faqLinks}
          />
          <FaqLink title="Payments" links={contactUsConfig.payment.faqLinks} />
          <FaqLink title="Other" links={contactUsConfig.other.faqLinks} />
        </div>
      </div>
      <ContactUsForm isLoggedIn={!!customer} />
    </>
  );
}
